import React, { useEffect, useRef, useState, useMemo } from 'react'
import { AlertMsg, AuthAvatar, MwButton, MwMultiSelector, MwSpinnerButton } from '../ui'
import { useCurrent, usePut, useFetch, usePost } from '../../hooks'
import { getCatalogLabel, getPriorityAppearance, getPriorityLabel, getStatusCategory, getStatusId, getTicketStatusFlow } from '../../constants/ticket'
import StatusFlow from '../ui/StatusFlow'
import { useTranslation } from 'react-i18next';
import { FaFilePdf, FaFileAlt, FaCalendarAlt, FaTag, FaBoxOpen, FaTimes, FaDownload } from "react-icons/fa";
import axios from 'axios';
import { useNavigate, useParams } from 'react-router-dom';
import { toSafeHtml } from '../../utils/richTextHtml';
import { buildAuthRequestConfig } from '../../utils/apiClient';
import { downloadAttachment, getAttachmentInfo as getSharedAttachmentInfo } from '../../utils/attachments';
import AttachmentsManager from '../ui/AttachmentsManager';
import LangSwitch from '../ui/LangSwitch';
import AbsatBotBadge, { isAbsatBot } from '../ui/AbsatBotBadge';
import { hasBothLanguages, localizedText } from '../../utils/localized';
import { buildAssigneesPayload, formatAssigneeNames, getAssigneeDisplayName, getTicketAssignees, toAssigneeOptions } from '../../utils/ticketAssignees';

const FilePopup = ({ url, name, ext, isPdf, isImage, isVideo, previewUrl, onClose }) => {
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [onClose]);

  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, zIndex: 9999,
      background: 'rgba(8,12,20,0.82)', backdropFilter: 'blur(10px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        background: '#fff', borderRadius: 20, overflow: 'hidden',
        display: 'flex', flexDirection: 'column',
        maxWidth: '92vw', maxHeight: '90vh', minWidth: 360,
        boxShadow: '0 32px 80px rgba(0,0,0,0.35)'
      }}>
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '14px 20px', borderBottom: '1px solid #f1f5f9', background: '#fafafa', gap: 16
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
            <div style={{
              width: 38, height: 38, borderRadius: 10, flexShrink: 0,
              background: isPdf ? '#fff1f2' : '#f0f9ff',
              color: isPdf ? '#ef4444' : '#0ea5e9',
              display: 'flex', alignItems: 'center', justifyContent: 'center'
            }}>
              {isPdf ? <FaFilePdf size={16} /> : <FaFileAlt size={16} />}
            </div>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#1e293b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 300 }}>{name}</div>
              <div style={{ fontSize: 10, color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1 }}>{ext} file</div>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
            <a href={url} download={name} target="_blank" rel="noreferrer" onClick={e => { e.preventDefault(); e.stopPropagation(); downloadAttachment(url, name); }} style={{
              display: 'flex', alignItems: 'center', gap: 6, padding: '7px 14px', borderRadius: 8,
              background: '#0f172a', color: '#fff', fontSize: 11, fontWeight: 700, textDecoration: 'none'
            }}>
              <FaDownload size={9} /> تحميل
            </a>
            <button onClick={onClose} style={{
              width: 32, height: 32, borderRadius: '50%', background: '#f1f5f9',
              border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#64748b'
            }}>
              <FaTimes size={12} />
            </button>
          </div>
        </div>
        <div style={{
          flex: 1, overflow: 'auto', display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: 24, background: '#f8fafc', minHeight: 220
        }}>
          {isImage ? (
            <img src={previewUrl || url} alt={name} style={{ maxWidth: '100%', maxHeight: '72vh', borderRadius: 12, boxShadow: '0 4px 24px rgba(0,0,0,0.12)', objectFit: 'contain' }} />
          ) : isVideo ? (
            <video controls playsInline style={{ width: '70vw', maxWidth: 980, maxHeight: '72vh', borderRadius: 12, background: '#000', boxShadow: '0 4px 24px rgba(0,0,0,0.12)' }}>
              <source src={url} type={ext === 'webm' ? 'video/webm' : ext === 'ogg' ? 'video/ogg' : 'video/mp4'} />
            </video>
          ) : isPdf ? (
            <iframe src={url} title={name} style={{ width: '70vw', height: '72vh', minWidth: 300, border: 'none', borderRadius: 10 }} />
          ) : (
            <div style={{ textAlign: 'center', padding: '40px 60px' }}>
              <div style={{ width: 72, height: 72, borderRadius: 20, background: '#f0f9ff', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
                <FaFileAlt size={30} color="#0ea5e9" />
              </div>
              <p style={{ color: '#64748b', fontSize: 13, marginBottom: 12 }}>لا يمكن معاينة هذا الملف</p>
              <a href={url} download={name} target="_blank" rel="noreferrer" onClick={e => { e.preventDefault(); downloadAttachment(url, name); }} style={{ color: '#6366f1', fontWeight: 700, fontSize: 13 }}>تحميل الملف</a>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const normalizeAttachmentList = (attachments) => (
  Array.isArray(attachments)
    ? attachments
    : attachments
      ? [attachments]
      : []
).filter(Boolean);

const AvatarFallback = ({ src, name, style, textStyle }) => {
  const [failed, setFailed] = useState(false);
  const value = String(src || '').trim();
  const label = String(name || '?').trim();
  const initial = (label || '?').charAt(0).toUpperCase();

  useEffect(() => {
    setFailed(false);
  }, [value]);

  if (value && !failed) {
    return (
      <img
        src={value}
        alt={label}
        style={style}
        onError={() => setFailed(true)}
      />
    );
  }

  return (
    <span
      title={label}
      style={{
        ...style,
        background: '#e2e8f0',
        color: '#475569',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontWeight: 800,
        ...textStyle
      }}
    >
      {initial}
    </span>
  );
};

const TicketInfo = ({ item, loading, error, supportSettings, onStatusUpdated }) => {
  const [t] = useTranslation('global');
  const { currentLangId, user } = useCurrent();
  const navigate = useNavigate();
  const params = useParams();
  const [popupOpen, setPopupOpen] = useState('');
  // لغة عرض العنوان والمحتوى (لو التذكرة مكتوبة بالعربي والإنجليزي).
  const [viewLang, setViewLang] = useState(currentLangId === 'en' ? 'en' : 'ar');
  const { putData, loading: statusLoading } = usePut();
  const { putData: assignTicket, loading: assignLoading } = usePut();
  const { postData: createDetail } = usePost(false);
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [statusMessage, setStatusMessage] = useState('');
  const [statusError, setStatusError] = useState('');
  
  const isTruthyFlag = (value) => value === true || value === 'true' || value === 1 || value === '1';
  const canAssignTickets =
    user?.role === 'admin' ||
    (isTruthyFlag(user?.supportAdminEnabled) &&
      (isTruthyFlag(user?.supportCanAssignTickets) || isTruthyFlag(user?.supportSuperAdminEnabled)));
    
  const { data: adminsResult } = useFetch(canAssignTickets ? `${process.env.REACT_APP_Auth_API_BASE_URL}/users/support-admins` : null);

  const normalizeUploadsBaseUrl = (value) => String(value || '').replace(/\/+$/, '');
  const normalizeAvatarUrl = (raw) => {
    const value = String(raw || '').trim();
    if (!value) return '';
    if (/^https?:\/\//i.test(value) || value.startsWith('blob:') || value.startsWith('data:')) return value;
    const cleaned = value.replace(/^\/+/, '');
    const uploadsBaseUrl = normalizeUploadsBaseUrl(process.env.REACT_APP_UPLOADS_API_BASE_URL);
    return uploadsBaseUrl ? `${uploadsBaseUrl}/${cleaned}` : cleaned;
  };

  const adminOptions = useMemo(() => {
    const admins = adminsResult?.data || [];
    return [
      { label: currentLangId === 'ar' ? 'غير معين' : 'Unassigned', value: '', lang: currentLangId },
      ...admins.map(admin => ({
        label: admin.name || admin.username || admin.email || admin._id,
        value: admin._id,
        lang: currentLangId,
        subLabel: admin?.jobTitle || '',
        avatar: normalizeAvatarUrl(admin?.userPic || admin?.avatar || admin?.image || admin?.profileImage || admin?.photo || ''),
        email: admin?.email || ''
      }))
    ];
  }, [adminsResult, currentLangId]);

  const ticketAssignees = useMemo(() => getTicketAssignees(item), [item]);
  const [draftAssignees, setDraftAssignees] = useState([]);
  const [assignError, setAssignError] = useState('');

  const resetDraftAssignees = () => {
    setDraftAssignees(toAssigneeOptions(item, adminOptions, currentLangId));
    setAssignError('');
  };

  useEffect(() => {
    setDraftAssignees(toAssigneeOptions(item, adminOptions, currentLangId));
    setAssignError('');
  }, [adminOptions, item, currentLangId]);

  const assigneeIdsKey = (ids) => [...ids].map(String).sort().join(',');
  const assigneesDirty =
    assigneeIdsKey(draftAssignees.map((option) => option.value)) !==
    assigneeIdsKey(ticketAssignees.map((assignee) => assignee.userId));

  const getCurrentUserId = () => {
    const fromUser = user?._id || user?.id || user?.userId || user?.user?._id || user?.user?.id || user?.user?.userId;
    if (fromUser) return String(fromUser);
    try {
      const persistedUser = JSON.parse(localStorage.getItem('user') || 'null');
      const persistedId = persistedUser?._id || persistedUser?.id || persistedUser?.userId || persistedUser?.user?._id || persistedUser?.user?.id || persistedUser?.user?.userId;
      if (persistedId) return String(persistedId);
    } catch { }
    try {
      const token = localStorage.getItem('token');
      if (!token) return null;
      const parts = token.split('.');
      if (parts.length < 2) return null;
      const payload = parts[1].replace(/-/g, '+').replace(/_/g, '/');
      const padded = payload + '='.repeat((4 - (payload.length % 4)) % 4);
      const json = atob(padded);
      const data = JSON.parse(json);
      const tokenId = data?._id || data?.id || data?.userId || data?.sub || null;
      if (tokenId) return String(tokenId);
    } catch { }
    return null;
  };

  const formatDateTime = (date) => {
    if (!date || Number.isNaN(date.getTime())) return '-';
    return date.toLocaleString(currentLangId === 'ar' ? 'ar-EG' : 'en-US', {
      year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false
    });
  };

  const handleSaveAssignees = async () => {
    if (!canAssignTickets || !item?._id || !assigneesDirty) return;
    const previousIds = new Set(ticketAssignees.map((assignee) => assignee.userId));
    const nextIds = new Set(draftAssignees.map((option) => String(option.value)));
    const added = draftAssignees.filter((option) => !previousIds.has(String(option.value)));
    const removed = ticketAssignees.filter((assignee) => !nextIds.has(assignee.userId));
    const currentUserId = getCurrentUserId();
    // الموظف الذي أزال نفسه من التذكرة قد يفقد صلاحية رؤيتها، فنرجعه لقائمة التذاكر
    const shouldLeaveAfterAssign = Boolean(currentUserId && previousIds.has(currentUserId) && !nextIds.has(currentUserId));
    const payload = buildAssigneesPayload(draftAssignees);
    setAssignError('');
    try {
      try {
        await assignTicket(`${process.env.REACT_APP_SUPPORT_API_BASE_URL}/admin/${item._id}/assign`, payload);
      } catch (assignErr) {
        if (assignErr?.response?.status !== 404) throw assignErr;
        await assignTicket(`${process.env.REACT_APP_SUPPORT_API_BASE_URL}/admin/${item._id}`, payload);
      }

      const createdBy = getCurrentUserId();
      const addedNames = formatAssigneeNames(added.map((option) => option.label), currentLangId);
      const removedNames = formatAssigneeNames(removed.map((assignee) => getAssigneeDisplayName(assignee, adminOptions)), currentLangId);
      if (createdBy && (addedNames || removedNames)) {
        const assignedByName = user?.name || user?.fullName || user?.username || user?.email || '';
        const assignedAt = formatDateTime(new Date());
        const parts = currentLangId === 'ar'
          ? [
            addedNames ? `تم تعيين التذكرة لـ ${addedNames}` : '',
            removedNames ? `${addedNames ? 'وإلغاء' : 'تم إلغاء'} تعيين ${removedNames}` : ''
          ]
          : [
            addedNames ? `Ticket assigned to ${addedNames}` : '',
            removedNames ? `${addedNames ? 'and unassigned' : 'Ticket unassigned from'} ${removedNames}` : ''
          ];
        const content = currentLangId === 'ar'
          ? `${parts.filter(Boolean).join(' ')} بتاريخ ${assignedAt}${assignedByName ? ` بواسطة ${assignedByName}` : ''}`
          : `${parts.filter(Boolean).join(' ')} at ${assignedAt}${assignedByName ? ` by ${assignedByName}` : ''}`;
        try {
          await createDetail(`${process.env.REACT_APP_SUPPORT_API_BASE_URL}/details`, {
            ticketId: item._id, content, attachments: null, createdBy, visibility: 'internal'
          });
        } catch (detailErr) {
          console.warn('Ticket assigned, but assignment note was not created:', detailErr);
        }
      }
      onStatusUpdated?.();
      if (shouldLeaveAfterAssign) {
        navigate(`/${params?.tenant || ''}/tickets`);
      }
    } catch (err) {
      console.error('Failed to assign ticket:', err);
      setAssignError(err?.response?.data?.message || (currentLangId === 'ar' ? 'فشل حفظ التعيين' : 'Failed to save assignment'));
    }
  };

  const attachmentList = normalizeAttachmentList(item?.attachments);
  const attachmentUrl = attachmentList[0] || '';
  const getAttachmentInfo = (url) => getSharedAttachmentInfo(url, item?.attachmentNames);
  const popupAttachment = getAttachmentInfo(popupOpen);
  const isImage = attachmentUrl ? getAttachmentInfo(attachmentUrl).isImage : false;
  const [previewUrl, setPreviewUrl] = useState(null);
  const objectUrlRef = useRef(null);

  useEffect(() => {
    if (objectUrlRef.current) { URL.revokeObjectURL(objectUrlRef.current); objectUrlRef.current = null; }
    setPreviewUrl(null);
    if (!attachmentUrl || !isImage) return;
    const controller = new AbortController();
    const token = localStorage.getItem('token');
    const headers = token ? { Authorization: `Bearer ${token}` } : undefined;
    fetch(attachmentUrl, { headers, signal: controller.signal })
      .then(r => r.blob()).then(blob => {
        const url = URL.createObjectURL(blob);
        objectUrlRef.current = url;
        setPreviewUrl(url);
      }).catch(() => { });
    return () => {
      controller.abort();
      if (objectUrlRef.current) { URL.revokeObjectURL(objectUrlRef.current); objectUrlRef.current = null; }
    };
  }, [attachmentUrl, isImage]);

  // إضافة / حذف ملف واحد من صفحة عرض التذكرة.
  const updateTicketAttachments = async (body) => {
    const response = await axios.put(
      `${process.env.REACT_APP_SUPPORT_API_BASE_URL}/admin/${item._id}/attachments`,
      body,
      buildAuthRequestConfig()
    );
    if (response?.data?.status !== 1) throw new Error(response?.data?.message || 'Failed');
    await onStatusUpdated?.();
  };

  const statusValue = getStatusId(supportSettings?.statuses, item?.statusId || item?.status);
  const statusFlow = getTicketStatusFlow(supportSettings?.statuses, statusValue, item, currentLangId);
  const priorityMeta = getPriorityAppearance(item?.priority || 'medium');
  const ticketVisibility = String(item?.visibility || 'external').toLowerCase() === 'internal' ? 'internal' : 'external';
  const isQuoteRequest = String(item?.requestType || '').toLowerCase() === 'quote-request';
  const quoteRequestMeta = {
    label: currentLangId === 'ar' ? 'طلب عرض توضيحي' : 'Quote Request',
    bg: '#fff7ed',
    color: '#c2410c',
    dot: '#f97316'
  };
  const ticketTypeMeta = ticketVisibility === 'internal'
    ? {
      label: currentLangId === 'ar' ? 'داخلية' : 'Internal',
      bg: '#eef2ff',
      color: '#4f46e5',
      dot: '#6366f1'
    }
    : {
      label: currentLangId === 'ar' ? 'خارجية' : 'External',
      bg: '#ecfdf5',
      color: '#047857',
      dot: '#10b981'
    };
  const statusOptions = (supportSettings?.statuses || [])
    .filter((statusItem) => statusItem?.isActive !== false)
    .map((statusItem) => ({
      label: currentLangId === 'ar' ? statusItem.labelAr : statusItem.labelEn,
      value: String(statusItem._id),
      lang: currentLangId
    }));

  useEffect(() => {
    const currentSelection = statusOptions.find((statusItem) => statusItem.value === String(statusValue || '')) || null;
    setSelectedStatus(currentSelection);
  }, [statusOptions, statusValue]);

  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 80 }}>
      <MwSpinnerButton isFullCenter withLabel={false} />
    </div>
  );

  if (!item) {
    return (
      <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden mb-6 p-8">
        <div className="text-sm font-bold text-slate-700">
          {currentLangId === 'ar' ? 'تعذر تحميل بيانات التذكرة' : 'Failed to load ticket'}
        </div>
        <div className="text-xs text-slate-500 mt-2">
          {error?.response?.status
            ? `${currentLangId === 'ar' ? 'كود الخطأ' : 'Status'}: ${error.response.status}`
            : (currentLangId === 'ar' ? 'تحقق من الاتصال أو إعدادات الـ API' : 'Check your API connectivity/config')}
        </div>
      </div>
    );
  }

  const updateStatusHandler = async () => {
    if (!item?._id || !selectedStatus?.value || selectedStatus.value === String(statusValue || '')) return;
    try {
      setStatusError('');
      setStatusMessage('');
      await putData(`${process.env.REACT_APP_SUPPORT_API_BASE_URL}/admin/${item._id}/status`, { statusId: selectedStatus.value });
      setStatusMessage(currentLangId === 'ar' ? 'تم تحديث حالة التذكرة بنجاح' : 'Ticket status updated successfully');
      onStatusUpdated?.();
    } catch (updateError) {
      setStatusError(updateError?.response?.data?.message || (currentLangId === 'ar' ? 'فشل تحديث حالة التذكرة' : 'Failed to update ticket status'));
    }
  };

  const createdAtLabel = (() => {
    const date = item?.createdAt ? new Date(item.createdAt) : null;
    if (!date || Number.isNaN(date.getTime())) return '-';
    return date.toLocaleString('en-US', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hour12: false });
  })();
  
  const expectedAtLabel = (() => {
    const date = item?.expectedAt ? new Date(item.expectedAt) : null;
    if (!date || Number.isNaN(date.getTime())) return '-';
    return date.toLocaleString('en-US', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hour12: false });
  })();

  const formatDurationMs = (valueMs) => {
    const ms = Math.max(0, Number(valueMs) || 0);
    const totalMinutes = Math.floor(ms / 60000);
    const days = Math.floor(totalMinutes / (60 * 24));
    const hours = Math.floor((totalMinutes % (60 * 24)) / 60);
    const minutes = totalMinutes % 60;
    if (currentLangId === 'ar') {
      const parts = [];
      if (days) parts.push(`${days}ي`);
      if (hours || days) parts.push(`${hours}س`);
      parts.push(`${minutes}د`);
      return parts.join(' ');
    }
    const parts = [];
    if (days) parts.push(`${days}d`);
    if (hours || days) parts.push(`${hours}h`);
    parts.push(`${minutes}m`);
    return parts.join(' ');
  };

  const statusCategory = getStatusCategory(supportSettings?.statuses, statusValue);
  const runtimeLabel = (() => {
    const processingAt = item?.processingAt
      ? new Date(item.processingAt)
      : statusCategory === 'processing' && (item?.modifiedAt || item?.updatedAt)
        ? new Date(item?.modifiedAt || item?.updatedAt)
        : null;
    const completedAt = item?.completedAt ? new Date(item.completedAt) : null;
    if (!processingAt || Number.isNaN(processingAt.getTime())) return '-';
    if (statusCategory === 'processing') {
      return formatDurationMs(Date.now() - processingAt.getTime());
    }
    if (statusCategory === 'completed') {
      if (!completedAt || Number.isNaN(completedAt.getTime())) return '-';
      return formatDurationMs(completedAt.getTime() - processingAt.getTime());
    }
    return '-';
  })();
  
  const updatedAtLabel = (() => {
    const raw = item?.modifiedAt || item?.updatedAt || null;
    const date = raw ? new Date(raw) : null;
    if (!date || Number.isNaN(date.getTime())) return '-';
    return date.toLocaleString('en-US', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hour12: false });
  })();

  const createdByLabel = (() => {
    if (isQuoteRequest) return item?.customerName || (currentLangId === 'ar' ? 'زائر الموقع' : 'Website visitor');
    const createdByRaw = item?.createdBy;
    if (!createdByRaw) return item?.customerName || '-';
    if (typeof createdByRaw === 'object') {
      return createdByRaw?.name || createdByRaw?.fullName || createdByRaw?.username || createdByRaw?.email || createdByRaw?._id || item?.customerName || '-';
    }
    const createdById = String(createdByRaw || '').trim();
    const customerId = item?.customerId != null ? String(item.customerId).trim() : '';
    if (createdById && customerId && createdById === customerId) {
      return item?.customerName || createdById;
    }
    const currentUserId = getCurrentUserId();
    if (createdById && currentUserId && createdById === String(currentUserId)) {
      return user?.name || user?.fullName || user?.username || user?.email || createdById;
    }
    if (item?.createdByName) return item.createdByName;
    const found = adminOptions.find((opt) => String(opt?.value || '') === createdById);
    if (found?.label) return found.label;
    return currentLangId === 'ar' ? 'فريق الدعم الفني' : 'Support Team';
  })();

  const createdByMeta = (() => {
    const createdByRaw = item?.createdBy;
    if (!createdByRaw) return { avatar: '', jobTitle: '' };
    if (typeof createdByRaw === 'object') {
      return {
        avatar: String(createdByRaw?.userPic || createdByRaw?.avatar || createdByRaw?.image || ''),
        jobTitle: String(createdByRaw?.jobTitle || createdByRaw?.subLabel || '')
      };
    }
    const createdById = String(createdByRaw || '').trim();
    const found = adminOptions.find((opt) => String(opt?.value || '') === createdById);
    return {
      avatar: String(found?.avatar || ''),
      jobTitle: String(found?.subLabel || '')
    };
  })();

  const assigneesDisplay = ticketAssignees.map((assignee) => {
    const found = adminOptions.find((opt) => String(opt?.value || '') === assignee.userId);
    return {
      userId: assignee.userId,
      label: getAssigneeDisplayName(assignee, adminOptions),
      avatar: String(found?.avatar || ''),
      jobTitle: String(found?.subLabel || '')
    };
  });

  const projectManagerLabel = (() => {
    const managerId = item?.projectManagerId ? String(item.projectManagerId).trim() : '';
    if (!managerId) return '';
    const name = item?.projectManagerName ? String(item.projectManagerName).trim() : '';
    if (name) return name;
    const found = adminOptions.find((opt) => String(opt?.value || '') === managerId);
    if (found?.label) return found.label;
    return managerId;
  })();

  const projectManagerMeta = (() => {
    const managerId = item?.projectManagerId ? String(item.projectManagerId).trim() : '';
    if (!managerId) return { avatar: '', jobTitle: '' };
    const found = adminOptions.find((opt) => String(opt?.value || '') === managerId);
    return {
      avatar: String(found?.avatar || ''),
      jobTitle: String(found?.subLabel || '')
    };
  })();

  // تم تعديل المصفوفة: رقم التذكرة أولاً وبخط عريض، وإزالة المربعات غير المطلوبة
  const metaItems = [
    { icon: <FaTag size={11} />, label: t('support.tickets.ticketNumber'), value: item?.ticketNumber || '-', isBold: true },
    ...(isQuoteRequest
      ? [
        { icon: <FaTag size={11} />, label: currentLangId === 'ar' ? 'الباقة' : 'Package', value: item?.packageTitle },
        { icon: <FaBoxOpen size={11} />, label: t('support.tickets.product'), value: item?.productName },
        { icon: <FaTag size={11} />, label: currentLangId === 'ar' ? 'الخدمة' : 'Service', value: item?.serviceName },
      ].filter(({ value }) => typeof value === 'string' && value.trim())
      : [
        { icon: <FaTag size={11} />, label: t('support.tickets.section'), value: getCatalogLabel(supportSettings?.sections, item?.sectionId, currentLangId) },
        { icon: <FaBoxOpen size={11} />, label: t('support.tickets.product'), value: getCatalogLabel(supportSettings?.products, item?.ProductId, currentLangId) },
      ]),
    { icon: <FaTag size={11} />, label: t('support.tickets.priority'), value: getPriorityLabel(item?.priority || 'medium', currentLangId) },
    { icon: <FaCalendarAlt size={11} />, label: currentLangId === 'ar' ? 'الوقت المتوقع' : 'Expected time', value: expectedAtLabel },
    ...(statusCategory === 'processing'
      ? [{ icon: <FaCalendarAlt size={11} />, label: currentLangId === 'ar' ? 'من جاري التنفيذ حتى الآن' : 'Processing time', value: runtimeLabel }]
      : statusCategory === 'completed'
        ? [{ icon: <FaCalendarAlt size={11} />, label: currentLangId === 'ar' ? 'من جاري التنفيذ إلى مكتملة' : 'Time to complete', value: runtimeLabel }]
        : []),
  ];
  const headerMetaRowStyle = {
    fontSize: 11,
    fontWeight: 700,
    display: 'grid',
    gridTemplateColumns: '22px minmax(0, max-content)',
    alignItems: 'center',
    columnGap: 6,
    minHeight: 22
  };
  const headerMetaIconStyle = {
    width: 22,
    height: 22,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0
  };
  const headerAvatarStyle = {
    width: 18,
    height: 18,
    borderRadius: '50%',
    objectFit: 'cover',
    border: '1px solid #e2e8f0'
  };

  return (
    <>
      {popupOpen && (
        <FilePopup url={popupAttachment.url} name={popupAttachment.name} ext={popupAttachment.ext}
          isPdf={popupAttachment.isPdf} isImage={popupAttachment.isImage} isVideo={popupAttachment.isVideo} previewUrl={popupAttachment.url === attachmentUrl ? previewUrl : null}
          onClose={() => setPopupOpen('')} />
      )}

      <div style={{ fontFamily: "'Cairo', 'Segoe UI', sans-serif", direction: currentLangId === 'ar' ? 'rtl' : 'ltr', display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div style={{ background: '#fff', borderRadius: 16, border: '1px solid #e2e8f0', overflow: 'visible' }}>
          <div style={{ height: 4, background: 'linear-gradient(90deg,#6366f1,#38bdf8)' }} />

          <div style={{ padding: '20px 24px' }}>
            {/* شريط الحالة: جديدة ← جاري التنفيذ ← تحت المراجعة ← مكتملة، وأي حالة تانية بتوقف الشريط عندها. */}
            <div style={{ marginBottom: 16 }}>
              <StatusFlow
                {...statusFlow}
                dir={currentLangId === 'ar' ? 'rtl' : 'ltr'}
                ariaLabel={currentLangId === 'ar' ? 'حالة التذكرة' : 'Ticket status'}
              />
            </div>
            {/* الترتيب الجديد: العميل ثم بواسطة ثم معينة للموظف */}
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, marginBottom: 18, flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                  <h2 dir="auto" style={{ margin: 0, fontSize: 17, fontWeight: 800, color: '#0f172a', lineHeight: 1.4, unicodeBidi: 'plaintext', textAlign: 'start' }}>
                    {localizedText(item, 'subject', viewLang)}
                  </h2>
                  {hasBothLanguages(item, ['subject', 'content']) && (
                    <LangSwitch value={viewLang} onChange={setViewLang} isAr={currentLangId !== 'en'} filled={{ ar: true, en: true }} />
                  )}
                </div>
                {(item?.tenantName || item?.tenantId) && (
                  <div style={{ ...headerMetaRowStyle, color: '#64748b' }}>
                    <span style={headerMetaIconStyle}><FaTag size={10} /></span>
                    <span>{currentLangId === 'ar' ? `العميل: ${item?.tenantName || item?.tenantId}` : `Customer: ${item?.tenantName || item?.tenantId}`}</span>
                  </div>
                )}
                {isAbsatBot(item) ? (
                  <div style={{ ...headerMetaRowStyle, color: '#64748b' }}>
                    <span>{currentLangId === 'ar' ? 'بواسطة:' : 'By:'}</span>
                    <AbsatBotBadge scheduled={Boolean(item?.scheduledJobId)} lang={currentLangId} />
                  </div>
                ) : (
                  <div style={{ ...headerMetaRowStyle, color: '#64748b' }}>
                    <span style={headerMetaIconStyle}>
                      <AvatarFallback src={createdByMeta?.avatar} name={createdByLabel} style={headerAvatarStyle} textStyle={{ fontSize: 9 }} />
                    </span>
                    <span>{currentLangId === 'ar' ? `بواسطة: ${createdByLabel}` : `By: ${createdByLabel}`}</span>
                  </div>
                )}
                {createdByMeta?.jobTitle ? (
                  <div style={{ fontSize: 10, color: '#94a3b8', fontWeight: 700, marginInlineStart: 28 }}>
                    {createdByMeta.jobTitle}
                  </div>
                ) : null}
                {assigneesDisplay.length > 0 && (
                  <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 6, fontSize: 11, fontWeight: 700, color: '#6366f1', minHeight: 22 }}>
                    <span>
                      {currentLangId === 'ar'
                        ? (assigneesDisplay.length > 1 ? 'معينة للموظفين:' : 'معينة للموظف:')
                        : 'Assigned to:'}
                    </span>
                    {assigneesDisplay.map((assignee) => (
                      <span
                        key={assignee.userId}
                        title={assignee.jobTitle || assignee.label}
                        style={{ display: 'inline-flex', alignItems: 'center', gap: 5, paddingBlock: 2, paddingInlineStart: 3, paddingInlineEnd: 8, borderRadius: 999, background: '#eef2ff', border: '1px solid #e0e7ff' }}
                      >
                        <AuthAvatar src={assignee.avatar} alt={assignee.label} size={18} fontSize={9} style={{ border: '1px solid #e2e8f0' }} />
                        <span>{assignee.label}</span>
                        {assignee.jobTitle ? <span style={{ fontSize: 10, color: '#94a3b8' }}>· {assignee.jobTitle}</span> : null}
                      </span>
                    ))}
                  </div>
                )}
                {item?.projectManagerId && (
                  <div style={{ ...headerMetaRowStyle, color: '#0f766e' }}>
                    <span style={headerMetaIconStyle}>
                      <AvatarFallback src={projectManagerMeta?.avatar} name={projectManagerLabel} style={headerAvatarStyle} textStyle={{ fontSize: 9 }} />
                    </span>
                    <span>{currentLangId === 'ar' ? `مدير المشروع: ${projectManagerLabel}` : `Project Manager: ${projectManagerLabel}`}</span>
                  </div>
                )}
                {item?.projectManagerId ? (
                  <div style={{ fontSize: 10, color: '#0f766e', fontWeight: 800, marginInlineStart: 28 }}>
                    {currentLangId === 'ar' ? 'مدير المشروع' : 'Project Manager'}
                    {projectManagerMeta?.jobTitle ? ` - ${projectManagerMeta.jobTitle}` : ''}
                  </div>
                ) : null}
              </div>
              
              {/* تاريخ الإنشاء والتحديث فوق شارة الحالة */}
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 10 }}>
                <div style={{ display: 'flex', gap: 12, fontSize: 11, color: '#64748b', fontWeight: 600 }}>
                  <span>{currentLangId === 'ar' ? `تاريخ الإنشاء: ${createdAtLabel}` : `Created: ${createdAtLabel}`}</span>
                  <span>{currentLangId === 'ar' ? `تحديث: ${updatedAtLabel}` : `Updated: ${updatedAtLabel}`}</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 8, flexWrap: 'wrap' }}>
                  <span style={{
                    display: 'inline-flex', alignItems: 'center', gap: 6,
                    padding: '5px 13px', borderRadius: 100,
                    background: ticketTypeMeta.bg, color: ticketTypeMeta.color, fontSize: 12, fontWeight: 700, flexShrink: 0
                  }}>
                    <span style={{ width: 7, height: 7, borderRadius: '50%', background: ticketTypeMeta.dot }} />
                    {ticketTypeMeta.label}
                  </span>
                  <span style={{
                    display: 'inline-flex', alignItems: 'center', gap: 6,
                    padding: '5px 13px', borderRadius: 100,
                    background: priorityMeta.bg, color: priorityMeta.color, fontSize: 12, fontWeight: 700, flexShrink: 0
                  }}>
                    <span style={{ width: 7, height: 7, borderRadius: '50%', background: priorityMeta.dot }} />
                    {getPriorityLabel(item?.priority || 'medium', currentLangId)}
                  </span>
                  {isQuoteRequest && (
                    <span style={{
                      display: 'inline-flex', alignItems: 'center', gap: 6,
                      padding: '5px 13px', borderRadius: 100,
                      background: quoteRequestMeta.bg, color: quoteRequestMeta.color, fontSize: 12, fontWeight: 800, flexShrink: 0
                    }}>
                      <span style={{ width: 7, height: 7, borderRadius: '50%', background: quoteRequestMeta.dot }} />
                      {quoteRequestMeta.label}
                    </span>
                  )}
                </div>
                {canAssignTickets && (
                  <div style={{ width: 320, maxWidth: '100%' }}>
                    <div style={{ border: '1px solid #e2e8f0', background: '#f8fafc', borderRadius: 12, padding: '10px 12px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, marginBottom: 8 }}>
                        <div style={{ fontSize: 11, fontWeight: 800, color: '#64748b' }}>
                          {currentLangId === 'ar' ? 'تعيين لموظفي الدعم' : 'Assign to Support Admins'}
                        </div>
                        {draftAssignees.length > 0 ? (
                          <span style={{ fontSize: 10, fontWeight: 800, color: '#6366f1', background: '#eef2ff', borderRadius: 999, padding: '1px 8px' }}>
                            {draftAssignees.length}
                          </span>
                        ) : null}
                      </div>
                      <MwMultiSelector
                        withLabel={false}
                        initalValue={currentLangId === 'ar' ? 'غير معينة — اختر موظف أو أكثر...' : 'Unassigned — select admins...'}
                        _data={adminOptions}
                        selectedItems={draftAssignees}
                        setSelectedItems={setDraftAssignees}
                        disabled={assignLoading}
                      />
                      {assigneesDirty && (
                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 6, marginTop: 8 }}>
                          <button
                            type="button"
                            onClick={resetDraftAssignees}
                            disabled={assignLoading}
                            style={{ padding: '5px 12px', borderRadius: 8, border: '1px solid #e2e8f0', background: '#fff', color: '#64748b', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}
                          >
                            {currentLangId === 'ar' ? 'إلغاء' : 'Cancel'}
                          </button>
                          <button
                            type="button"
                            onClick={handleSaveAssignees}
                            disabled={assignLoading}
                            style={{ padding: '5px 12px', borderRadius: 8, border: 'none', background: '#6366f1', color: '#fff', fontSize: 11, fontWeight: 800, cursor: assignLoading ? 'wait' : 'pointer', opacity: assignLoading ? 0.7 : 1 }}
                          >
                            {assignLoading
                              ? (currentLangId === 'ar' ? 'جاري الحفظ...' : 'Saving...')
                              : (currentLangId === 'ar' ? 'حفظ التعيين' : 'Save assignment')}
                          </button>
                        </div>
                      )}
                      {assignError ? (
                        <div style={{ marginTop: 6, fontSize: 11, fontWeight: 700, color: '#dc2626' }}>{assignError}</div>
                      ) : null}
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 10 }}>
              {metaItems.map(({ icon, label, value, isBold }, i) => (
                <div key={i} style={{
                  display: 'flex', alignItems: 'center', gap: 10,
                  padding: '10px 14px', borderRadius: 10,
                  background: '#f8fafc', border: '1px solid #e2e8f0'
                }}>
                  <div style={{
                    width: 32, height: 32, borderRadius: 8, flexShrink: 0,
                    background: '#eef2ff', color: '#6366f1',
                    display: 'flex', alignItems: 'center', justifyContent: 'center'
                  }}>
                    {icon}
                  </div>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 9, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 2 }}>{label}</div>
                    {/* تطبيق الخط العريض على رقم التذكرة */}
                    <div style={{ fontSize: 13, fontWeight: isBold ? 800 : 600, color: '#1e293b', ...(isQuoteRequest ? { whiteSpace: 'normal', overflowWrap: 'anywhere' } : { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }) }}>{value}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div style={{ background: '#fff', borderRadius: 16, border: '1px solid #e2e8f0' }}>
          <div style={{
            padding: '12px 24px', borderBottom: '1px solid #f1f5f9',
            fontSize: 10, fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 1.4
          }}>
            {t('support.tickets.subject') || 'محتوى التذكرة'}
          </div>

          <div style={{ padding: '20px 24px' }}>
            <style>{`
              .mw-ticket-html ol { list-style: decimal; padding-inline-start: 20px; margin: 0; }
              .mw-ticket-html ul { list-style: disc; padding-inline-start: 20px; margin: 0; }
              .mw-ticket-html li { margin: 7px 0; padding-inline-start: 4px; }
              .mw-ticket-html p { margin: 0 0 10px; }
              .mw-ticket-html p:last-child { margin-bottom: 0; }
              .mw-ticket-html div { margin-bottom: 8px; }
              .mw-ticket-html div:last-child { margin-bottom: 0; }
              .mw-ticket-html a { color: #4f46e5; text-decoration: underline; }
              .mw-ticket-html,
              .mw-ticket-html * {
                max-width: 100%;
                overflow-wrap: anywhere;
                word-break: break-word;
                white-space: normal;
              }
            `}</style>
            <div
              className="mw-rich-content mw-ticket-html"
              dir="auto"
              style={{ margin: 0, fontSize: 14, lineHeight: 1.85, color: '#334155', unicodeBidi: 'plaintext', textAlign: 'start', maxWidth: '100%', overflowX: 'hidden', whiteSpace: 'normal', overflowWrap: 'anywhere', wordBreak: 'break-word' }}
              dangerouslySetInnerHTML={{ __html: toSafeHtml(localizedText(item, 'content', viewLang)) }}
            />

            <div style={{ marginTop: 22, paddingTop: 18, borderTop: '1px dashed #e2e8f0' }}>
              <AttachmentsManager
                attachments={attachmentList}
                attachmentNames={item?.attachmentNames}
                isAr={currentLangId === 'ar'}
                title={t('support.tickets.attachments')}
                inputId={`ticket_attachments_${item?._id || ''}`}
                onOpen={(url) => setPopupOpen(url)}
                getImageSrc={(url) => (url === attachmentUrl ? (previewUrl || url) : url)}
                onAdd={(files) => updateTicketAttachments({ add: files.map((file) => file.url), attachmentNames: files })}
                onRemove={(url) => updateTicketAttachments({ remove: [url] })}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
export default TicketInfo

